import { Article } from '../types';

export const nercTariffBandsExplained: Article = {
    id: 'nerc-tariff-bands-explained',
    title: 'NERC Electricity Tariff Bands Explained: Are You Overpaying?',
    slug: 'nerc-electricity-tariff-bands-explained',
    excerpt: 'Understand how the Nigerian Electricity Regulatory Commission (NERC) categorizes neighborhoods into tariff bands and how to calculate your costs.',
    category: 'Power Generation',
    date: 'February 11, 2026',
    readTime: '2 min read',
    author: 'power.ng Research',
    trending: false,
    keywords: ['NERC tariff bands', 'Band A electricity', 'Nigeria power bills', 'DisCos', 'electricity cost'],
    metaDescription: 'Learn everything about the NERC Electricity Tariff Bands (A, B, C, D, E) in Nigeria. Find out how many hours of power you are guaranteed and if you are being overbilled.',
    content: `## Quick Answer: What are NERC Tariff Bands?
The Nigerian Electricity Regulatory Commission (NERC) classifies electricity consumers into **five distinct bands (A, B, C, D, and E)** based on the minimum number of hours of electricity they receive daily. **Band A** customers receive the highest supply (minimum 20 hours/day) and pay the highest rates per kilowatt-hour (kWh) without government subsidies.

---

## Discovering Your Band

Since the removal of blanket electricity subsidies, understanding your band is essential for managing your monthly household or business budget. The Distribution Companies (DisCos) such as IKEDC, EKEDC, and AEDC use these bands to structure their billing.

### The 5 Tariff Bands Defined

* **Band A:** Customers are guaranteed a minimum of **20 hours** of power supply daily. They currently pay the highest commercial rate per kWh and do not receive government subsidies.
* **Band B:** Customers are guaranteed a minimum of **16 hours** of power supply daily.
* **Band C:** Customers are guaranteed a minimum of **12 hours** of power supply daily.
* **Band D:** Customers are guaranteed a minimum of **8 hours** of power supply daily.
* **Band E:** Customers are guaranteed a minimum of **4 hours** of power supply daily.

*(Note: Rates for Bands B-E are typically subsidized to protect lower-income and rural households from the full brunt of generation costs.)*

---

## How to Tell if You Are Overpaying

Many customers complain about remaining on Band A despite receiving less than the mandated 20 hours of power. If you are a Band A customer and your feeder consistently under-delivers, NERC regulations state that your area should be downgraded to the appropriate band (e.g., Band B or C) with a corresponding drop in your kWh price.

### Steps to Verify Your Band
1. **Check Your Meter / Receipt:** Most modern prepaid meter vending receipts will print your current Tariff Band and your cost per unit directly on the paper or token SMS.
2. **Visit Your DisCo's Website:** All DisCos are mandated to publish the list of Band A feeders on their websites. You can search for your street or transformer to verify its official classification.
3. **Calculate the Supply:** Log the hours of electricity you receive over a 7-day period.

### How to File a Complaint
If you are being billed at a Band A rate but receiving Band C supply levels, do the following:
1. File an official complaint with your local DisCo office.
2. Ensure you receive a tracking number for your ticket.
3. If the issue is not resolved within 15 days, escalate the complaint directly to the **NERC Forum Office** in your state.`
  };
